import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@core/store/hooks';
import { Dropdown } from '@core/ui';
import { useActiveCompany } from '../hooks';
import { setActiveCompany } from '../store/companySlice';

/**
 * Active-company control for the app header.
 *
 * Renders nothing until there is something to choose: a centre with a single company
 * (the default one every organization gets) never sees it. With two or more it is
 * a `Dropdown` of the companies, and choosing one dispatches `setActiveCompany`, which
 * re-scopes every company-scoped request through the `X-Company` header.
 */
export function CompanySwitcher() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { uuid, company, companies, canSwitch } = useActiveCompany();

  if (!canSwitch || !company) {
    return null;
  }

  const items = companies.map((candidate) => ({
    key: candidate.uuid,
    label: candidate.name,
    checked: candidate.uuid === uuid,
    onSelect: () => {
      if (candidate.uuid !== uuid) {
        dispatch(setActiveCompany(candidate.uuid));
      }
    },
  }));

  return (
    <Dropdown label={t('company.switcher.change')} items={items} className="gap-x-1.5 px-2.5 text-sm">
      <Icon icon="heroicons:building-office" className="h-4 w-4 shrink-0" aria-hidden="true" />
      {/* A visually-hidden label, not aria-label: the trigger's name is the action
          ("change company"); this says what the visible text IS. */}
      <span className="sr-only">{t('company.switcher.label')}</span>
      <span className="max-w-[28vw] truncate font-medium text-[var(--color-text)] sm:max-w-[160px]">
        {company.name}
      </span>
      {/* The same chevron as the user menu: two triggers side by side in the header
          must say "this opens" the same way. */}
      <Icon icon="heroicons:chevron-down" aria-hidden="true" className="size-4 shrink-0" />
    </Dropdown>
  );
}
